import { atom } from "jotai";
import { MarkerType, type Edge, type ReactFlowInstance } from "@xyflow/react";
import type { NoxusReactflowNodeV2 } from "@/types/spotflow-v2";
import type { ClipboardData, DynamicNodeConfig } from "../types";
import {
  nodesAtom,
  edgesAtom,
  dynamicNodeConfigsAtom,
  pendingNodeConfigRefreshAtom,
  isEditorReadOnlyAtom,
} from "../atoms";
import { markDirty, remapNodeIds, uniqueFlowIOName } from "../helpers";
import { recordTransaction } from "../history/recordTransaction";
import {
  isFlowInputNode,
  isFlowOutputNode,
} from "@/components/SpotflowV2/constants";

const CLIPBOARD_KEY = "workflow-v2-clipboard";
const PASTE_OFFSET_STEP = 30;

interface PasteOffsetTracker {
  lastX: number;
  lastY: number;
  count: number;
}

const pasteOffsetTracker: PasteOffsetTracker = {
  lastX: 0,
  lastY: 0,
  count: 0,
};

export function normalizeClipboardNodes(
  nodes: NoxusReactflowNodeV2[],
  canvasNodes: NoxusReactflowNodeV2[]
): NoxusReactflowNodeV2[] {
  const byId = new Map(canvasNodes.map((n) => [n.id, n]));
  const childrenByParent = new Map<string, NoxusReactflowNodeV2[]>();
  for (const n of canvasNodes) {
    if (!n.parentId) continue;
    const siblings = childrenByParent.get(n.parentId);
    if (siblings) siblings.push(n);
    else childrenByParent.set(n.parentId, [n]);
  }

  // Selection doesn't cascade to group children, so pull descendants
  // into the payload — pasting a container must bring its contents.
  const included = new Map(nodes.map((n) => [n.id, n]));
  const queue = nodes.map((n) => n.id);
  while (queue.length > 0) {
    const parentId = queue.pop()!;
    for (const child of childrenByParent.get(parentId) ?? []) {
      if (included.has(child.id)) continue;
      included.set(child.id, child);
      queue.push(child.id);
    }
  }

  // A child copied without its parent must carry an absolute position —
  // relative coordinates are meaningless once the parent is out of the
  // payload, and this is the only moment the parent chain is still around.
  return [...included.values()].map((n) => {
    if (!n.parentId || included.has(n.parentId)) return n;
    let x = n.position.x;
    let y = n.position.y;
    let ancestor = byId.get(n.parentId);
    while (ancestor) {
      x += ancestor.position.x;
      y += ancestor.position.y;
      ancestor = ancestor.parentId ? byId.get(ancestor.parentId) : undefined;
    }
    return {
      ...n,
      parentId: undefined,
      extent: undefined,
      position: { x, y },
    };
  });
}

export function snapshotDynamicConfigs(
  nodeIds: Iterable<string>,
  configs: Map<string, DynamicNodeConfig>
): Record<string, DynamicNodeConfig> {
  const out: Record<string, DynamicNodeConfig> = {};
  for (const id of nodeIds) {
    const cfg = configs.get(id);
    if (cfg) out[id] = cfg;
  }
  return out;
}

export function remapDynamicConfigKeys(
  configs: Record<string, DynamicNodeConfig>,
  idMap: Map<string, string>
): Map<string, DynamicNodeConfig> {
  const next = new Map<string, DynamicNodeConfig>();
  for (const [oldId, cfg] of Object.entries(configs)) {
    const newId = idMap.get(oldId);
    if (newId) next.set(newId, cfg);
  }
  return next;
}

function writeToLocalStorage(
  nodes: NoxusReactflowNodeV2[],
  allEdges: Edge[],
  canvasNodes: NoxusReactflowNodeV2[],
  dynamicConfigs: Map<string, DynamicNodeConfig>
) {
  const clipboardNodes = normalizeClipboardNodes(nodes, canvasNodes);
  const nodeIds = new Set(clipboardNodes.map((n) => n.id));
  const relevantEdges = allEdges.filter(
    (e) => nodeIds.has(e.source) && nodeIds.has(e.target)
  );
  const data: ClipboardData = {
    nodes: clipboardNodes,
    edges: relevantEdges,
    dynamicConfigs: snapshotDynamicConfigs(nodeIds, dynamicConfigs),
  };
  localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(data));
}

// ── Copy ──────────────────────────────────────────────────────────────────

export const copyNodesAtom = atom(
  null,
  (get, _set, nodes: NoxusReactflowNodeV2[]) => {
    if (nodes.length === 0) return;
    const allEdges = get(edgesAtom);
    writeToLocalStorage(
      nodes,
      allEdges,
      get(nodesAtom),
      get(dynamicNodeConfigsAtom)
    );
  }
);

// ── Cut ───────────────────────────────────────────────────────────────────

export const cutNodesAtom = atom(
  null,
  (get, set, nodes: NoxusReactflowNodeV2[]) => {
    if (get(isEditorReadOnlyAtom)) return;
    if (nodes.length === 0) return;

    const allEdges = get(edgesAtom);
    writeToLocalStorage(
      nodes,
      allEdges,
      get(nodesAtom),
      get(dynamicNodeConfigsAtom)
    );

    const nodeIds = new Set(nodes.map((n) => n.id));
    // Also remove children of cut groups
    const childIds = new Set(
      get(nodesAtom)
        .filter((n) => n.parentId && nodeIds.has(n.parentId))
        .map((n) => n.id)
    );
    const allRemovedIds = new Set([...nodeIds, ...childIds]);

    recordTransaction(
      get,
      set,
      `Cut ${allRemovedIds.size} node${allRemovedIds.size === 1 ? "" : "s"}`,
      ({ nodes, edges }) => {
        for (let i = nodes.length - 1; i >= 0; i--) {
          if (allRemovedIds.has(nodes[i].id)) nodes.splice(i, 1);
        }
        for (let i = edges.length - 1; i >= 0; i--) {
          const e = edges[i];
          if (allRemovedIds.has(e.source) || allRemovedIds.has(e.target)) {
            edges.splice(i, 1);
          }
        }
      }
    );
    markDirty(set);
  }
);

// ── Paste ─────────────────────────────────────────────────────────────────

export const pasteNodesAtom = atom(
  null,
  (
    get,
    set,
    args: {
      pastePosition?: { x: number; y: number };
      rfInstance: ReactFlowInstance | null;
    }
  ) => {
    if (get(isEditorReadOnlyAtom)) return;
    const { pastePosition, rfInstance } = args;
    const raw = localStorage.getItem(CLIPBOARD_KEY);
    if (!raw) return;

    let data: ClipboardData;
    try {
      data = JSON.parse(raw) as ClipboardData;
    } catch {
      return;
    }

    if (!data.nodes || data.nodes.length === 0) return;

    // Children with a parent in the payload have parent-relative positions:
    // they follow their container, so only top-level nodes get offset.
    const payloadIds = new Set(data.nodes.map((n) => n.id));
    const isTopLevel = (n: NoxusReactflowNodeV2) =>
      !n.parentId || !payloadIds.has(n.parentId);

    // Compute offset
    let offsetX = PASTE_OFFSET_STEP;
    let offsetY = PASTE_OFFSET_STEP;

    if (pastePosition && rfInstance) {
      const flowPos = rfInstance.screenToFlowPosition(pastePosition);

      // Find bounding box of clipboard nodes
      let minX = Infinity;
      let minY = Infinity;
      for (const n of data.nodes) {
        if (!isTopLevel(n)) continue;
        if (n.position.x < minX) minX = n.position.x;
        if (n.position.y < minY) minY = n.position.y;
      }

      // If same position as last paste, increment
      if (
        pastePosition.x === pasteOffsetTracker.lastX &&
        pastePosition.y === pasteOffsetTracker.lastY
      ) {
        pasteOffsetTracker.count += 1;
      } else {
        pasteOffsetTracker.count = 0;
        pasteOffsetTracker.lastX = pastePosition.x;
        pasteOffsetTracker.lastY = pastePosition.y;
      }

      const extraOffset = pasteOffsetTracker.count * PASTE_OFFSET_STEP;
      offsetX = flowPos.x - minX + extraOffset;
      offsetY = flowPos.y - minY + extraOffset;
    } else {
      pasteOffsetTracker.count += 1;
      offsetX = pasteOffsetTracker.count * PASTE_OFFSET_STEP;
      offsetY = pasteOffsetTracker.count * PASTE_OFFSET_STEP;
    }

    const offsetNodes: NoxusReactflowNodeV2[] = data.nodes.map((n) =>
      isTopLevel(n)
        ? {
            ...n,
            position: {
              x: n.position.x + offsetX,
              y: n.position.y + offsetY,
            },
          }
        : n
    );

    const {
      nodes: newNodes,
      edges: newEdges,
      idMap,
    } = remapNodeIds(offsetNodes, data.edges);

    const sourceConfigs =
      data.dynamicConfigs && Object.keys(data.dynamicConfigs).length > 0
        ? data.dynamicConfigs
        : snapshotDynamicConfigs(
            data.nodes.map((n) => n.id),
            get(dynamicNodeConfigsAtom)
          );
    const remappedConfigs = remapDynamicConfigKeys(sourceConfigs, idMap);
    if (remappedConfigs.size > 0) {
      set(dynamicNodeConfigsAtom, (prev) => {
        const next = new Map(prev);
        for (const [id, cfg] of remappedConfigs) next.set(id, cfg);
        return next;
      });
    }

    // Flow I/O names double as workflow keys and must stay unique per kind.
    // Dedup pasted Input/Output nodes against the live canvas, growing the
    // pool as we relabel so two pasted nodes don't land on the same name.
    const dedupPool = [...get(nodesAtom)];
    for (const n of newNodes) {
      if (
        !isFlowInputNode(n.data.nodeType) &&
        !isFlowOutputNode(n.data.nodeType)
      )
        continue;
      const unique = uniqueFlowIOName(
        dedupPool,
        n.data.nodeType,
        n.data.name as string
      );
      n.data.name = unique;
      const cfg = (n.data.nodeConfig ?? {}) as Record<string, unknown>;
      cfg.label = unique;
      n.data.nodeConfig = cfg;
      dedupPool.push(n);
    }

    const newEdgesWithType: Edge[] = newEdges.map((e) => ({
      ...e,
      type: "v2Edge",
      markerEnd: { type: MarkerType.ArrowClosed },
    }));

    // Deselect existing nodes, then add pasted ones
    recordTransaction(
      get,
      set,
      `Paste ${newNodes.length} node${newNodes.length === 1 ? "" : "s"}`,
      ({ nodes, edges }) => {
        for (const n of nodes) {
          (n as { selected?: boolean }).selected = false;
        }
        for (const n of newNodes) {
          nodes.push(n as unknown as (typeof nodes)[number]);
        }
        for (const e of newEdgesWithType) {
          edges.push(e as (typeof edges)[number]);
        }
      }
    );
    markDirty(set);

    const pastedIds = newNodes
      .filter((n) => n.type === "baseNodeV2")
      .map((n) => n.id);
    if (pastedIds.length > 0) {
      const pending = get(pendingNodeConfigRefreshAtom);
      const extra = pastedIds.filter((id) => !pending.includes(id));
      if (extra.length > 0) {
        set(pendingNodeConfigRefreshAtom, [...pending, ...extra]);
      }
    }
  }
);

// ── Has clipboard data ────────────────────────────────────────────────────

export function hasClipboardData(): boolean {
  const raw = localStorage.getItem(CLIPBOARD_KEY);
  if (!raw) return false;
  try {
    const data = JSON.parse(raw) as ClipboardData;
    return Array.isArray(data.nodes) && data.nodes.length > 0;
  } catch {
    return false;
  }
}
